import styled from '@emotion/styled'
import React, { useCallback, useEffect, useRef, useState } from 'react'

/* ── Types ── */

export type SocialNodeState = 'locked' | 'unlocked' | 'connected'

type Partner = {
  id: string
  name: string
  x: number
  y: number
  width: number
  height: number
}

type PathData = {
  element: SVGPathElement
  length: number
}

type OutgoingDot = {
  group: SVGGElement
  dot: SVGCircleElement
  tail: SVGLineElement
  gradient: SVGLinearGradientElement
  partnerId: string
  isLeftSide: boolean
  isBottomEntry: boolean
  pathElement: SVGPathElement
  pathLength: number
  tailLength: number
  progress: number
  delay: number
  started: boolean
  sweepTriggered: boolean
  sweepTriggerProgress: number
}

type IncomingDot = {
  element: SVGCircleElement
  startX: number
  startY: number
  targetX: number
  targetY: number
  progress: number
  pulseTriggered?: boolean
}

type ActiveSweep = {
  element: SVGRectElement
  gradient: SVGLinearGradientElement
  stops: SVGStopElement[]
  fromLeft?: boolean
  fromBottom?: boolean
  isVertical: boolean
  progress: number
}

type ActivePulse = {
  element: SVGCircleElement
  startRadius: number
  progress: number
}

/* ── Styled Components ── */

const Container = styled.div`
  position: relative;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  overflow: hidden;
`

const SvgWrap = styled.div`
  position: relative;
  width: 100%;
  min-height: 300px;
`

const NetworkSvg = styled.svg`
  width: 100%;
  height: auto;
  aspect-ratio: 700 / 340;
  display: block;

  .network-path {
    fill: none;
    stroke: #d1d5db;
    stroke-width: 1.5;
    opacity: 0.6;
    transition: opacity 0.2s ease, stroke 0.2s ease;
  }

  .network-path.is-highlighted {
    opacity: 1;
    stroke: #47c435;
    stroke-width: 2;
  }
`

const PartnerNode = styled.g`
  cursor: default;

  .node-bg {
    fill: white;
    stroke: #d1d5db;
    stroke-width: 1;
  }

  .node-label {
    fill: #374151;
    font-size: 10px;
    font-weight: 500;
    dominant-baseline: central;
    user-select: none;
  }
`

const CenterNode = styled.g`
  cursor: default;

  .node-glow {
    fill: #47c435;
    opacity: 0.12;
  }

  .node-bg {
    fill: #47c435;
  }

  .node-label {
    fill: white;
    font-size: 11px;
    font-weight: 600;
    text-anchor: middle;
    user-select: none;
  }
`

const SubmissionLabel = styled.g`
  .node-label {
    fill: #6b7280;
    font-size: 10px;
    font-weight: 500;
    text-anchor: middle;
    user-select: none;
  }
`

const VizTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-align: center;
  margin-bottom: 8px;
`

const VizDescription = styled.div`
  font-size: 11px;
  color: #6b7280;
  text-align: center;
  margin-bottom: 12px;
`

const VizFooter = styled.div`
  font-size: 11px;
  color: #9ca3af;
  text-align: center;
  margin-top: 8px;
  line-height: 1.5;
`

/* ── Constants ── */

const SVG_WIDTH = 700
const SVG_HEIGHT = 340
const CENTER_X = SVG_WIDTH / 2
const CENTER_Y = 120
const CENTER_RADIUS = 34
const SUBMISSION_Y = 30
const GREEN_COLOR = '#47c435'
const LEFT_ALIGN = 60
const RIGHT_ALIGN = 640
const NODE_HEIGHT = 24


// Default partner sites when no real data is available
const DEFAULT_PARTNERS: Partner[] = [
  { id: 'p1', name: 'Local News Site', x: LEFT_ALIGN + 50, y: 60, width: 100, height: NODE_HEIGHT },
  { id: 'p2', name: 'City Chamber', x: LEFT_ALIGN + 47.5, y: 110, width: 95, height: NODE_HEIGHT },
  { id: 'p3', name: 'Community Blog', x: LEFT_ALIGN + 52.5, y: 160, width: 105, height: NODE_HEIGHT },
  { id: 'p4', name: 'Downtown BID', x: LEFT_ALIGN + 49, y: 210, width: 98, height: NODE_HEIGHT },
  { id: 'p5', name: 'Tourism Board', x: RIGHT_ALIGN - 49, y: 60, width: 98, height: NODE_HEIGHT },
  { id: 'p6', name: 'Arts Council', x: RIGHT_ALIGN - 45, y: 110, width: 90, height: NODE_HEIGHT },
  { id: 'p7', name: 'Parks & Rec', x: RIGHT_ALIGN - 46, y: 160, width: 92, height: NODE_HEIGHT },
]

// Helper to calculate text width (approximate)
function estimateTextWidth(text: string): number {
  // Approximate width based on character count and average char width at 10px font
  return text.length * 6 + 12 // minimal padding
}

// Helper to build partner positions from network site names
function buildPartnersFromNames(names: string[]): Partner[] {
  if (names.length === 0) return DEFAULT_PARTNERS

  const partners: Partner[] = []
  const leftCount = Math.ceil(names.length / 2)
  const rightCount = names.length - leftCount

  // Position left column partners
  const leftYPositions = [60, 110, 160, 210]
  for (let i = 0; i < leftCount && i < 4; i++) {
    const name = names[i]
    const width = Math.max(80, estimateTextWidth(name))
    partners.push({
      id: `p${i + 1}`,
      name,
      x: LEFT_ALIGN + width / 2,
      y: leftYPositions[i],
      width,
      height: NODE_HEIGHT,
    })
  }

  // Position right column partners
  const rightYPositions = [60, 110, 160, 210]
  for (let i = 0; i < rightCount && i < 4; i++) {
    const name = names[leftCount + i]
    const width = Math.max(80, estimateTextWidth(name))
    partners.push({
      id: `p${leftCount + i + 1}`,
      name,
      x: RIGHT_ALIGN - width / 2,
      y: rightYPositions[i],
      width,
      height: NODE_HEIGHT,
    })
  }

  return partners
}

/* ── Easing Functions ── */

function easeInOutSine(t: number): number {
  return -(Math.cos(Math.PI * t) - 1) / 2
}

/* ── Main Component ── */

type NetworkSite = {
  id: string
  name: string
}

type Props = {
  className?: string
  networkSites?: NetworkSite[]
  totalSiteCount?: number
  facebookState?: SocialNodeState
  instagramState?: SocialNodeState
  onNavigateToSocial?: () => void
  onNavigateToBilling?: () => void
}

const NetworkFlowViz: React.FC<Props> = ({
  className,
  networkSites = [],
  totalSiteCount,
  facebookState: _facebookState = 'locked',
  instagramState: _instagramState = 'locked',
  onNavigateToSocial: _onNavigateToSocial,
  onNavigateToBilling: _onNavigateToBilling,
}) => {
  const partners = React.useMemo(() => {
    const names = networkSites.map(s => s.name)
    return buildPartnersFromNames(names)
  }, [networkSites])

  // All partners for animation (social nodes hidden until feature is ready)
  const allAnimatablePartners = React.useMemo(() => {
    return [...partners]
  }, [partners])

  const additionalCount = totalSiteCount ? Math.max(0, totalSiteCount - partners.length) : 0
  const svgRef = useRef<SVGSVGElement>(null)
  const animationRef = useRef<number | null>(null)
  const stateRef = useRef({
    currentPhase: 'idle' as 'idle' | 'incoming' | 'distributing',
    incomingDot: null as IncomingDot | null,
    outgoingDots: [] as OutgoingDot[],
    activeSweeps: [] as ActiveSweep[],
    activePulses: [] as ActivePulse[],
    pathMap: new Map<string, PathData>(),
    lastCycleEnd: 0,
    distributionStartTime: 0,
  })

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )

  // Build path data from SVG
  const buildPathData = useCallback(() => {
    const svg = svgRef.current
    if (!svg) return

    const paths = svg.querySelectorAll<SVGPathElement>('.network-path')
    stateRef.current.pathMap.clear()

    paths.forEach(path => {
      const partnerId = path.dataset.partner
      if (partnerId) {
        stateRef.current.pathMap.set(partnerId, {
          element: path,
          length: path.getTotalLength(),
        })
      }
    })
  }, [])

  // Create center pulse animation
  const createCenterPulse = useCallback(() => {
    const svg = svgRef.current
    if (!svg) return

    const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    ring.setAttribute('cx', String(CENTER_X))
    ring.setAttribute('cy', String(CENTER_Y))
    ring.setAttribute('r', String(CENTER_RADIUS))
    ring.setAttribute('fill', 'none')
    ring.setAttribute('stroke', GREEN_COLOR)
    ring.setAttribute('stroke-width', '3')
    ring.setAttribute('opacity', '0.6')
    ring.style.pointerEvents = 'none'

    const centerNode = svg.querySelector('.center-node')
    if (centerNode) {
      svg.insertBefore(ring, centerNode)
    } else {
      svg.appendChild(ring)
    }

    stateRef.current.activePulses.push({
      element: ring,
      startRadius: CENTER_RADIUS,
      progress: 0,
    })
  }, [])

  // Create partner sweep animation
  const createPartnerSweep = useCallback((partnerId: string, fromLeft: boolean, isVertical = false) => {
    const svg = svgRef.current
    if (!svg) return

    const partner = allAnimatablePartners.find(p => p.id === partnerId)
    if (!partner) return

    const partnerNode = svg.querySelector(`[data-id="${partnerId}"]`)
    if (!partnerNode) return

    const rect = partnerNode.querySelector('.node-bg')
    if (!rect) return

    let defs = svg.querySelector('defs')
    if (!defs) {
      defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
      svg.insertBefore(defs, svg.firstChild)
    }

    const rectX = parseFloat(rect.getAttribute('x') || '0')
    const rectY = parseFloat(rect.getAttribute('y') || '0')
    const rectW = parseFloat(rect.getAttribute('width') || '0')
    const rectH = parseFloat(rect.getAttribute('height') || '0')
    const rectRx = rect.getAttribute('rx') || '6'

    const gradientId = `sweep-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`
    const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient')
    gradient.setAttribute('id', gradientId)
    gradient.setAttribute('gradientUnits', 'userSpaceOnUse')

    if (isVertical) {
      gradient.setAttribute('x1', String(rectX + rectW / 2))
      gradient.setAttribute('x2', String(rectX + rectW / 2))
      gradient.setAttribute('y1', String(rectY))
      gradient.setAttribute('y2', String(rectY + rectH))
    } else {
      gradient.setAttribute('x1', String(rectX))
      gradient.setAttribute('x2', String(rectX + rectW))
      gradient.setAttribute('y1', String(rectY + rectH / 2))
      gradient.setAttribute('y2', String(rectY + rectH / 2))
    }

    const stops: SVGStopElement[] = []
    for (let i = 0; i < 4; i++) {
      const stop = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
      stop.setAttribute('offset', '0%')
      stop.setAttribute('stop-color', GREEN_COLOR)
      stop.setAttribute('stop-opacity', i === 1 || i === 2 ? '0.5' : '0')
      gradient.appendChild(stop)
      stops.push(stop)
    }
    defs.appendChild(gradient)

    const sweep = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    sweep.setAttribute('x', String(rectX))
    sweep.setAttribute('y', String(rectY))
    sweep.setAttribute('width', String(rectW))
    sweep.setAttribute('height', String(rectH))
    sweep.setAttribute('rx', rectRx)
    sweep.setAttribute('ry', rectRx)
    sweep.setAttribute('fill', `url(#${gradientId})`)
    sweep.style.pointerEvents = 'none'

    const labelEl = partnerNode.querySelector('.node-label')
    if (labelEl) {
      partnerNode.insertBefore(sweep, labelEl)
    } else {
      partnerNode.appendChild(sweep)
    }

    stateRef.current.activeSweeps.push({
      element: sweep,
      gradient,
      stops,
      fromLeft,
      fromBottom: isVertical,
      isVertical,
      progress: 0,
    })
  }, [allAnimatablePartners])

  // Start incoming phase - dot travels from submission label to center
  const startIncomingPhase = useCallback(() => {
    const svg = svgRef.current
    if (!svg) return

    stateRef.current.currentPhase = 'incoming'

    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    dot.setAttribute('r', '4')
    dot.setAttribute('cx', String(CENTER_X))
    dot.setAttribute('cy', String(SUBMISSION_Y))
    dot.setAttribute('fill', GREEN_COLOR)
    dot.setAttribute('opacity', '0')
    dot.style.filter = `drop-shadow(0 0 4px ${GREEN_COLOR})`

    const firstNode = svg.querySelector('.partner-node, .center-node, .submission-label')
    if (firstNode) {
      svg.insertBefore(dot, firstNode)
    } else {
      svg.appendChild(dot)
    }

    stateRef.current.incomingDot = {
      element: dot,
      startX: CENTER_X,
      startY: SUBMISSION_Y,
      targetX: CENTER_X,
      targetY: CENTER_Y,
      progress: 0,
    }
  }, [])

  // Start distributing phase - dots travel from center to partners
  const startDistributingPhase = useCallback(() => {
    const svg = svgRef.current
    if (!svg) return

    stateRef.current.currentPhase = 'distributing'
    stateRef.current.distributionStartTime = performance.now()

    const numPartners = Math.min(3 + Math.floor(Math.random() * 3), allAnimatablePartners.length)
    const shuffled = [...allAnimatablePartners].sort(() => Math.random() - 0.5)
    const selectedPartners = shuffled.slice(0, numPartners)

    let defs = svg.querySelector('defs')
    if (!defs) {
      defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
      svg.insertBefore(defs, svg.firstChild)
    }

    selectedPartners.forEach(partner => {
      const pathInfo = stateRef.current.pathMap.get(partner.id)
      if (!pathInfo) return

      const isSocialNode = partner.id.startsWith('social_')
      const isBottomEntry = partner.id === 'p8' || isSocialNode
      const isLeftSide = !isBottomEntry && partner.x < CENTER_X

      const group = document.createElementNS('http://www.w3.org/2000/svg', 'g')
      const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
      const tail = document.createElementNS('http://www.w3.org/2000/svg', 'line')

      const gradientId = `tail-g-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`
      const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient')
      gradient.setAttribute('id', gradientId)
      gradient.setAttribute('gradientUnits', 'userSpaceOnUse')

      const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
      stop1.setAttribute('offset', '0%')
      stop1.setAttribute('stop-color', GREEN_COLOR)
      stop1.setAttribute('stop-opacity', '0')

      const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
      stop2.setAttribute('offset', '100%')
      stop2.setAttribute('stop-color', GREEN_COLOR)
      stop2.setAttribute('stop-opacity', '0.7')

      gradient.appendChild(stop1)
      gradient.appendChild(stop2)
      if (defs) defs.appendChild(gradient)

      dot.setAttribute('r', '3.5')
      dot.setAttribute('fill', GREEN_COLOR)
      dot.style.filter = `drop-shadow(0 0 3px ${GREEN_COLOR})`

      tail.setAttribute('stroke', `url(#${gradientId})`)
      tail.setAttribute('stroke-width', '3')
      tail.setAttribute('stroke-linecap', 'round')

      group.setAttribute('opacity', '0')
      group.appendChild(tail)
      group.appendChild(dot)

      const connectionsGroup = svg.querySelector('.connections')
      if (connectionsGroup && connectionsGroup.nextSibling) {
        svg.insertBefore(group, connectionsGroup.nextSibling)
      } else {
        const nodesGroup = svg.querySelector('.partner-node, .center-node, .submission-label')
        if (nodesGroup) {
          svg.insertBefore(group, nodesGroup)
        } else {
          svg.appendChild(group)
        }
      }

      const dotRadius = 3.5
      const sweepTriggerProgress = Math.min(0.98, 1 - dotRadius / pathInfo.length)

      stateRef.current.outgoingDots.push({
        group,
        dot,
        tail,
        gradient,
        partnerId: partner.id,
        isLeftSide,
        isBottomEntry,
        pathElement: pathInfo.element,
        pathLength: pathInfo.length,
        tailLength: 12,
        progress: 0,
        delay: Math.random() * 300,
        started: false,
        sweepTriggered: false,
        sweepTriggerProgress,
      })
    })
  }, [allAnimatablePartners])

  // Update incoming dot
  const updateIncoming = useCallback(
    (delta: number) => {
      const dot = stateRef.current.incomingDot
      if (!dot) return

      const incomingDuration = 1800
      dot.progress += delta / incomingDuration

      if (!dot.pulseTriggered && dot.progress >= 0.92) {
        dot.pulseTriggered = true
        createCenterPulse()
      }

      if (dot.progress >= 1) {
        dot.element.remove()
        stateRef.current.incomingDot = null
        startDistributingPhase()
        return
      }

      const ease = easeInOutSine(dot.progress)
      const x = dot.startX + (dot.targetX - dot.startX) * ease
      const y = dot.startY + (dot.targetY - dot.startY) * ease
      const dotOpacity = dot.progress < 0.2 ? dot.progress / 0.2 : 1

      dot.element.setAttribute('cx', String(x))
      dot.element.setAttribute('cy', String(y))
      dot.element.setAttribute('opacity', String(dotOpacity))
    },
    [createCenterPulse, startDistributingPhase]
  )

  // Update outgoing dots
  const updateOutgoing = useCallback(
    (delta: number) => {
      const now = performance.now()
      const outgoingDuration = 2800
      let allComplete = true

      stateRef.current.outgoingDots = stateRef.current.outgoingDots.filter(dotInfo => {
        if (!dotInfo.started) {
          if (now - stateRef.current.distributionStartTime > dotInfo.delay) {
            dotInfo.started = true
          } else {
            allComplete = false
            return true
          }
        }

        dotInfo.progress += delta / outgoingDuration

        if (!dotInfo.sweepTriggered && dotInfo.progress >= dotInfo.sweepTriggerProgress) {
          dotInfo.sweepTriggered = true
          if (dotInfo.isBottomEntry) {
            createPartnerSweep(dotInfo.partnerId, false, true)
          } else {
            createPartnerSweep(dotInfo.partnerId, !dotInfo.isLeftSide)
          }
        }

        if (dotInfo.progress >= 1) {
          dotInfo.group.remove()
          dotInfo.gradient.remove()
          return false
        }

        allComplete = false

        const pathProgress = 1 - dotInfo.progress
        const headPoint = dotInfo.pathElement.getPointAtLength(pathProgress * dotInfo.pathLength)

        const tailOffset = dotInfo.tailLength / dotInfo.pathLength
        const tailProgress = Math.min(1, pathProgress + tailOffset)
        const tailPoint = dotInfo.pathElement.getPointAtLength(tailProgress * dotInfo.pathLength)

        let dotOpacity: number
        if (dotInfo.progress < 0.15) {
          dotOpacity = dotInfo.progress / 0.15
        } else if (dotInfo.progress > 0.85) {
          dotOpacity = (1 - dotInfo.progress) / 0.15
        } else {
          dotOpacity = 1
        }

        dotInfo.dot.setAttribute('cx', String(headPoint.x))
        dotInfo.dot.setAttribute('cy', String(headPoint.y))
        dotInfo.tail.setAttribute('x1', String(tailPoint.x))
        dotInfo.tail.setAttribute('y1', String(tailPoint.y))
        dotInfo.tail.setAttribute('x2', String(headPoint.x))
        dotInfo.tail.setAttribute('y2', String(headPoint.y))
        dotInfo.gradient.setAttribute('x1', String(tailPoint.x))
        dotInfo.gradient.setAttribute('y1', String(tailPoint.y))
        dotInfo.gradient.setAttribute('x2', String(headPoint.x))
        dotInfo.gradient.setAttribute('y2', String(headPoint.y))
        dotInfo.group.setAttribute('opacity', String(dotOpacity))

        return true
      })

      if (allComplete && stateRef.current.outgoingDots.length === 0) {
        stateRef.current.currentPhase = 'idle'
        stateRef.current.lastCycleEnd = performance.now()
      }
    },
    [createPartnerSweep]
  )

  // Update sweeps
  const updateSweeps = useCallback((delta: number) => {
    const sweepDuration = 300

    stateRef.current.activeSweeps = stateRef.current.activeSweeps.filter(sweep => {
      sweep.progress += delta / sweepDuration

      if (sweep.progress >= 1) {
        sweep.element.remove()
        sweep.gradient.remove()
        return false
      }

      const bandWidth = 0.35
      let bandCenter: number

      if (sweep.isVertical) {
        if (sweep.fromBottom) {
          bandCenter = 1 + bandWidth / 2 - sweep.progress * (1 + bandWidth)
        } else {
          bandCenter = -bandWidth / 2 + sweep.progress * (1 + bandWidth)
        }
      } else {
        if (sweep.fromLeft) {
          bandCenter = -bandWidth / 2 + sweep.progress * (1 + bandWidth)
        } else {
          bandCenter = 1 + bandWidth / 2 - sweep.progress * (1 + bandWidth)
        }
      }

      const bandStart = Math.max(0, bandCenter - bandWidth / 2) * 100
      const bandPeak1 = Math.max(0, Math.min(100, (bandCenter - bandWidth / 4) * 100))
      const bandPeak2 = Math.max(0, Math.min(100, (bandCenter + bandWidth / 4) * 100))
      const bandEnd = Math.min(100, (bandCenter + bandWidth / 2) * 100)

      sweep.stops[0].setAttribute('offset', `${bandStart}%`)
      sweep.stops[1].setAttribute('offset', `${bandPeak1}%`)
      sweep.stops[2].setAttribute('offset', `${bandPeak2}%`)
      sweep.stops[3].setAttribute('offset', `${bandEnd}%`)

      return true
    })
  }, [])

  // Update pulses
  const updatePulses = useCallback((delta: number) => {
    const pulseDuration = 400

    stateRef.current.activePulses = stateRef.current.activePulses.filter(pulse => {
      pulse.progress += delta / pulseDuration

      if (pulse.progress >= 1) {
        pulse.element.remove()
        return false
      }

      const expandAmount = 20
      const currentRadius = pulse.startRadius + pulse.progress * expandAmount
      const opacity = 0.6 * (1 - pulse.progress)

      pulse.element.setAttribute('r', String(currentRadius))
      pulse.element.setAttribute('opacity', String(opacity))

      return true
    })
  }, [])

  // Main animation loop
  useEffect(() => {
    if (prefersReducedMotion) return

    buildPathData()

    let lastTime = performance.now()
    const cyclePause = 1500

    const animate = (now: number) => {
      const delta = now - lastTime

      updateSweeps(delta)
      updatePulses(delta)

      if (stateRef.current.currentPhase === 'idle') {
        if (now - stateRef.current.lastCycleEnd > cyclePause) {
          startIncomingPhase()
        }
      } else if (stateRef.current.currentPhase === 'incoming') {
        updateIncoming(delta)
      } else if (stateRef.current.currentPhase === 'distributing') {
        updateOutgoing(delta)
      }

      lastTime = now
      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [
    prefersReducedMotion,
    buildPathData,
    updateSweeps,
    updatePulses,
    startIncomingPhase,
    updateIncoming,
    updateOutgoing,
  ])

  // Listen for reduced motion preference
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    query.addEventListener('change', handler)
    return () => query.removeEventListener('change', handler)
  }, [])

  // Generate curved paths from partners to center
  // Paths connect to different Y positions along the center circle edge (within vertical third)
  // and flatten out (become horizontal) as they approach the center
  const generatePath = (partner: Partner): string => {
    const isBottomEntry = partner.id === 'p8'

    const isLeftSide = partner.x < CENTER_X

    if (isBottomEntry) {
      // Straight vertical path for bottom node
      return `M ${CENTER_X} ${partner.y - partner.height / 2} L ${CENTER_X} ${CENTER_Y + CENTER_RADIUS}`
    }

    // Calculate partner edge position
    const partnerX = isLeftSide ? partner.x + partner.width / 2 : partner.x - partner.width / 2
    const partnerY = partner.y

    // Calculate center connection point - spread within vertical third of circle
    // Left partners (p1-p4): indices 0-3, Right partners (p5-p7): indices 4-6
    const leftPartners = partners.filter(p => p.x < CENTER_X && p.id !== 'p8')
    const rightPartners = partners.filter(p => p.x > CENTER_X && p.id !== 'p8')

    let centerEdgeY: number
    const verticalSpread = CENTER_RADIUS * 0.3 // Spread within ~30% of radius (compact vertical third)

    if (isLeftSide) {
      const idx = leftPartners.findIndex(p => p.id === partner.id)
      const count = leftPartners.length
      // Distribute evenly within the vertical spread
      const yOffset = count > 1 ? ((idx / (count - 1)) - 0.5) * 2 * verticalSpread : 0
      centerEdgeY = CENTER_Y + yOffset
    } else {
      const idx = rightPartners.findIndex(p => p.id === partner.id)
      const count = rightPartners.length
      const yOffset = count > 1 ? ((idx / (count - 1)) - 0.5) * 2 * verticalSpread : 0
      centerEdgeY = CENTER_Y + yOffset
    }

    // Calculate the X position on the circle edge at the given Y
    // Circle equation: (x - cx)² + (y - cy)² = r²
    // Solving for x: x = cx ± sqrt(r² - (y - cy)²)
    const yDiff = centerEdgeY - CENTER_Y
    const xOffset = Math.sqrt(Math.max(0, CENTER_RADIUS * CENTER_RADIUS - yDiff * yDiff))
    const centerEdgeX = isLeftSide ? CENTER_X - xOffset : CENTER_X + xOffset

    // Curve control points - designed to flatten out (become horizontal) near center
    // First control point: extends horizontally from partner
    const ctrlOffset = 60 + Math.abs(partnerY - CENTER_Y) * 0.3
    const ctrl1X = partnerX + (isLeftSide ? ctrlOffset : -ctrlOffset)
    const ctrl1Y = partnerY

    // Second control point: positioned to make path enter center horizontally
    // Place it directly horizontal from the center edge point, further out
    const ctrl2X = centerEdgeX + (isLeftSide ? -50 : 50)
    const ctrl2Y = centerEdgeY // Same Y as entry point = horizontal entry

    return `M ${partnerX} ${partnerY} C ${ctrl1X} ${ctrl1Y} ${ctrl2X} ${ctrl2Y} ${centerEdgeX} ${centerEdgeY}`
  }

  return (
    <Container className={className}>
      <VizTitle>Your Events Reach the Network</VizTitle>
      <VizDescription>When you submit events, they automatically distribute to community sites in your area</VizDescription>

      <SvgWrap>
        <NetworkSvg ref={svgRef} viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} preserveAspectRatio="xMidYMid meet">
          {/* Connection paths */}
          <g className="connections">
            {partners.map(partner => (
              <path key={partner.id} className="network-path" data-partner={partner.id} d={generatePath(partner)} />
            ))}
            {/* Submission path */}
            <path className="network-path submission-path" d={`M ${CENTER_X} ${SUBMISSION_Y + 15} L ${CENTER_X} ${CENTER_Y - CENTER_RADIUS}`} />

            {/* Social media paths hidden until feature is ready */}
          </g>

          {/* Submission label */}
          <SubmissionLabel className="submission-label">
            <text className="node-label" x={CENTER_X} y={SUBMISSION_Y - 8}>
              You submit an event
            </text>
            <text className="node-label" x={CENTER_X} y={SUBMISSION_Y + 6}>
              to your website
            </text>
          </SubmissionLabel>

          {/* Center node - Your Website */}
          <CenterNode className="center-node">
            <circle className="node-glow" cx={CENTER_X} cy={CENTER_Y} r={48} />
            <circle className="node-bg" cx={CENTER_X} cy={CENTER_Y} r={CENTER_RADIUS} />
            <text className="node-label" x={CENTER_X} y={CENTER_Y - 5}>
              Your
            </text>
            <text className="node-label" x={CENTER_X} y={CENTER_Y + 9}>
              Website
            </text>
          </CenterNode>

          {/* Partner nodes */}
          {partners.map(partner => {
            const rectX = partner.x - partner.width / 2
            const rectY = partner.y - partner.height / 2

            return (
              <PartnerNode key={partner.id} className="partner-node" data-id={partner.id}>
                <rect className="node-bg" x={rectX} y={rectY} width={partner.width} height={partner.height} rx={6} />
                <text className="node-label" x={partner.x} y={partner.y} textAnchor="middle">
                  {partner.name}
                </text>
              </PartnerNode>
            )
          })}

          {/* Additional sites count node */}
          {additionalCount > 0 && (
            <PartnerNode className="partner-node additional-sites">
              <rect
                className="node-bg"
                x={CENTER_X - 55}
                y={248}
                width={110}
                height={NODE_HEIGHT}
                rx={6}
                style={{ fill: '#f9fafb', stroke: '#e5e7eb' }}
              />
              <text
                className="node-label"
                x={CENTER_X}
                y={260}
                textAnchor="middle"
                style={{ fontStyle: 'italic', fill: '#9ca3af' }}
              >
                + {additionalCount} more sites
              </text>
            </PartnerNode>
          )}

          {/* Social media nodes hidden until feature is ready */}
        </NetworkSvg>
      </SvgWrap>
      <VizFooter>
        Your events are automatically shared with community calendars in your area.
        <br />
        Each site can review and approve your events to feature on their public calendar.
      </VizFooter>
    </Container>
  )
}

export default NetworkFlowViz
